// frontend/src/pages/motivos-mba/MotivosMbaListPage.jsx
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import PermissionDeniedAlert from '../../components/common/PermissionDeniedAlert';
import AlertException from '../../components/common/AlertException';
import AppSelect from '../../components/common/AppSelect';
import { useAuth } from '../../contexts/AuthContext';

const SEARCH_DEBOUNCE_MS = 450;

function MotivosMbaListPage() {
  const { loading: authLoading, hasPermission } = useAuth();

  const canManage = hasPermission('config.motivos_mba');
  const canView   = hasPermission('motivos_mba.view') || canManage;
  const canCreate = hasPermission('motivos_mba.create') || canManage;
  const canEdit   = hasPermission('motivos_mba.edit') || canManage;
  const canDelete = hasPermission('motivos_mba.delete') || canManage;

  const [motivosMba, setMotivosMba] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [paginationInfo, setPaginationInfo] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);
  const perPageOptions = [
    { value: 20, label: '20' },
    { value: 50, label: '50' },
    { value: 'all', label: 'Todos' },
  ];

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (authLoading) return;

    const controller = new AbortController();
    abortRef.current = controller;

    const fetchMotivosMba = async () => {
      setLoading(true);
      setError(null);

      if (!canView) {
        setError({ kind: 'permission' });
        setLoading(false);
        return;
      }

      try {
        const response = await api.get('/motivos-mba', {
          signal: controller.signal,
          params: {
            page: currentPage,
            per_page: perPage === 'all' ? 10000 : Number(perPage),
            status: statusFilter,
            search: debouncedSearch || undefined,
          },
        });
        setMotivosMba(response?.data?.data ?? []);
        setPaginationInfo(response?.data?.meta ?? null);
      } catch (err) {
        if (controller.signal.aborted) return;
        if (err?.response?.status === 403) {
          setError({ kind: 'permission' });
        } else {
          setError(err);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    fetchMotivosMba();
    return () => controller.abort();
  }, [authLoading, canView, currentPage, debouncedSearch, perPage, statusFilter]);

  const handlePageChange = (newPage) => {
    if (!paginationInfo) return;
    if (newPage < 1 || newPage > paginationInfo.last_page) return;
    setCurrentPage(newPage);
  };

  const totalRegistrosPaginacao = Number.isFinite(Number(paginationInfo?.total))
    ? Number(paginationInfo.total)
    : motivosMba.length;

  const handlePerPageChange = (value) => {
    setPerPage(value);
    setCurrentPage(1);
  };

  const handleDelete = async (id) => {
    if (!canDelete) return;

    if (window.confirm('Tem certeza de que deseja desativar este motivo MBA?')) {
      try {
        await api.delete(`/motivos-mba/${id}`);
        setMotivosMba((prev) => prev.filter((m) => m.id !== id));
      } catch (err) {
        const errorMsg = err?.response?.data?.message || 'Ocorreu um erro ao desativar o motivo MBA.';
        alert(errorMsg);
      }
    }
  };

  const handleRestore = async (id) => {
    if (!canDelete) return;

    if (window.confirm('Tem certeza de que deseja reativar este motivo MBA?')) {
      try {
        await api.patch(`/motivos-mba/${id}/restore`);
        setMotivosMba((prev) => prev.filter((m) => m.id !== id));
      } catch {
        alert('Ocorreu um erro ao reativar o motivo MBA.');
      }
    }
  };

  if (authLoading) {
    return (
      <div className="motivos-mba-page">
        <LoadingSpinner text="Verificando sessão..." />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="motivos-mba-page">
        <LoadingSpinner text="Carregando motivos MBA..." />
      </div>
    );
  }

  if (error) {
    if (error.kind === 'permission') {
      return (
        <div className="motivos-mba-page">
          <PermissionDeniedAlert
            requiredPermission="motivos_mba.view"
            action="visualizar a lista de motivos MBA"
          />
        </div>
      );
    }
    return (
      <div className="motivos-mba-page">
        <AlertException error={error} fallback="Não foi possível carregar os motivos MBA." />
      </div>
    );
  }

  return (
    <div className="motivos-mba-page">
      <div className="content-header">
        <h1 className="content-title">Motivos MBA</h1>
        <p className="content-subtitle">Gerenciamento de motivos para mandados de busca e apreensão</p>
      </div>

      <div className="card motivos-mba-list-card">
        <div className="card-body">
          <div className="list-toolbar motivos-mba-list-toolbar d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
            <div className="motivos-mba-filters-group d-flex align-items-center gap-3">
              <div className="input-group motivos-mba-search-group">
                <span className="input-group-text"><i className="bi bi-search"></i></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Buscar por motivo..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    if (currentPage !== 1) {
                      setCurrentPage(1);
                    }
                  }}
                />
              </div>
              <div className="motivos-mba-status-filter d-flex align-items-center gap-2">
                <label htmlFor="motivosMbaStatusFilter" className="mb-0 motivos-mba-status-label">Filtrar por:</label>
                <select
                  id="motivosMbaStatusFilter"
                  className="form-select"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  aria-label="Filtrar motivos MBA por status"
                >
                  <option value="active">Ativos</option>
                  <option value="inactive">Inativos</option>
                </select>
              </div>
              <div className="d-flex align-items-center gap-2">
                <span className="text-secondary small">Registros por página</span>
                <AppSelect
                  className="motivos-mba-per-page-select"
                  ariaLabel="Registros por página"
                  options={perPageOptions}
                  value={perPage}
                  onChange={handlePerPageChange}
                />
              </div>
            </div>

            {canCreate && (
              <Link to="/painel/motivos-mba/novo" className="btn btn-primary">
                <i className="bi bi-plus-lg me-2"></i>
                Novo Motivo MBA
              </Link>
            )}
          </div>

          <div className="table-responsive">
            <table className="table table-dark-custom table-stack">
              <thead>
                <tr>
                  <th>Motivo</th>
                  <th>Status</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {motivosMba.length > 0 ? (
                  motivosMba.map((m) => {
                    const isInactive = Boolean(m.deleted_at);

                    return (
                      <tr key={m.id}>
                        <td data-label="Motivo">{m.motivo}</td>
                        <td data-label="Status">{isInactive ? 'Inativo' : 'Ativo'}</td>
                        <td className="table-actions motivos-mba-table-actions d-flex gap-2" data-label="Ações">
                          {canEdit && !isInactive && (
                            <Link
                              to={`/painel/motivos-mba/${m.id}/editar`}
                              className="btn btn-sm btn-outline-warning"
                              title="Editar"
                            >
                              <i className="bi bi-pencil"></i>
                            </Link>
                          )}

                          {canDelete && !isInactive && (
                            <span
                              title={!m.can_delete ? 'Este registro não pode ser desativado por estar relacionado a uma Movimentação' : 'Desativar'}
                              style={{ display: 'inline-block' }}
                            >
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleDelete(m.id)}
                                disabled={!m.can_delete}
                                style={!m.can_delete ? { pointerEvents: 'none' } : {}}
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            </span>
                          )}

                          {canDelete && isInactive && (
                            <button
                              className="btn btn-sm btn-outline-success"
                              title="Reativar"
                              onClick={() => handleRestore(m.id)}
                            >
                              <i className="bi bi-person-check"></i>
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="3" className="text-center">Nenhum motivo MBA encontrado.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {paginationInfo && paginationInfo.last_page > 1 && perPage !== 'all' && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <span className="motivos-mba-pagination-summary">
                Página <strong>{paginationInfo.current_page}</strong> de{' '}
                <strong>{paginationInfo.last_page}</strong> -{' '}
                <strong>{totalRegistrosPaginacao.toLocaleString('pt-BR')}</strong> registros
              </span>
              <div className="btn-group">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <i className="bi bi-chevron-left"></i> Anterior
                </button>
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === paginationInfo.last_page}
                >
                  Próximo <i className="bi bi-chevron-right"></i>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MotivosMbaListPage;
